/* Dependencies */
// Services
import { AddToCartRequest } from '../../Services/ShopifyService/ShopifyService.model';
import { shopifyService } from '../../Services/ShopifyService/ShopifyService.service';
import { TrapFocus } from '../../Utils/TrapFocus/TrapFocus';

// Modules
import { VariantSelects } from '../VariantSelects/VariantSelects';
import { ProductWidgetElements } from './ProductWidget.model';

/**
 * Adds quick add logic to a product card.
 * @class
 */
class ProductWidget {
  public trapFocus: TrapFocus;
  public elements: ProductWidgetElements = {
    triggerWidget: null,
    container: null,
    buttonCloseWidget: null,
    addToCartButton: null,
    addToCartButtonText: null,
    addToCartForm: null,
    addToCartQuantitySelect: null,
    rechargeFrequency: null,
    rechargeUnitType: null,
    rechargePurchaseType: null,
  };
  private initialVariantId: number;
  private isOpen: boolean = false;
  private variantSelects: VariantSelects;

  constructor(parentElement: HTMLElement) {
    const container: HTMLElement = parentElement.querySelector(
      '[data-product-widgets]'
    );

    this.elements = {
      triggerWidget: parentElement.querySelector('[data-widget-trigger]'),
      container,
      buttonCloseWidget: parentElement.querySelector('[data-widget-close]'),
      addToCartForm: container.querySelector('form'),
      addToCartQuantitySelect: container.querySelector('[data-quantity]'),
      addToCartButton: container.querySelector('button'),
      addToCartButtonText: container.querySelector('[name="add"] > span'),
      rechargeFrequency: container.querySelector(
        '[name="properties[shipping_interval_frequency]'
      ),
      rechargeUnitType: container.querySelector(
        '[name="properties[shipping_interval_unit_type]'
      ),
      rechargePurchaseType: container.querySelector(
        'input[name=purchase_type]:checked'
      ),
    };

    const variantContainer = container.querySelector(
      '[data-product-variant]'
    ) as HTMLElement;

    if (variantContainer) {
      this.variantSelects = new VariantSelects(variantContainer, () => {
        this.onVariantChange();
      });
    }

    this.initialVariantId = parseInt(
      (container.querySelector('[name=id]') as HTMLInputElement).value
    );

    // Define trap focus
    this.trapFocus = new TrapFocus(container, {
      escapeClose: true,
      escapeCloseCB: () => {
        this.closeWidget();
      },
    });

    // Attach events
    this.bindEvents();
  }

  /* ===================================
  Display/Hide widget
  ====================================*/
  /**
   * Opens the widget
   */
  openWidget(handleTrapFocus = false): void {
    // Show the widget
    this.elements.container.classList.remove('tw-hidden');
    this.elements.triggerWidget.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    // Set trap focus if keyboard event.
    if (handleTrapFocus) {
      this.trapFocus.attachTrapFocus();
    }

    // Bind the global click out event.
    setTimeout(() => {
      this.bindGlobalClickOut();
    });
  }

  /**
   * Closes the widget
   */
  closeWidget(): void {
    this.elements.container.classList.add('tw-hidden');
    this.elements.triggerWidget.setAttribute('aria-expanded', 'false');
    this.isOpen = false;

    // Remove focus trap
    if (this.trapFocus.activeState) {
      this.trapFocus.dettachTrapFocus();
    }

    // Remove the global click out event.
    this.dettachGlobalClickOut();
  }

  /**
   * Toggles the widget open / closed.
   * @param event - The input event.
   */
  toggle(event: Event): void {
    event.preventDefault();
    // Check for a keyboard event.
    const handleTrapFocus = this.trapFocus.checkKeyboardEvent(event);

    if (this.isOpen) {
      this.closeWidget();
    } else {
      this.openWidget(handleTrapFocus);
    }
  }

  /**
   * Binds the related events for the widget.
   */
  bindEvents(): void {
    this.elements.addToCartForm.addEventListener('submit', (event) => {
      this.onSubmitHandler(event);
    });

    if (this.elements.triggerWidget) {
      this.elements.triggerWidget.addEventListener(
        'click',
        this.toggle.bind(this)
      );
    }

    if (this.elements.buttonCloseWidget) {
      this.elements.buttonCloseWidget.addEventListener(
        'click',
        this.toggle.bind(this)
      );
    }
  }

  /**
   * Binds a global click out event.
   */
  bindGlobalClickOut(): void {
    window.addEventListener('click', this.onClickOutOfWidget);
  }

  /**
   * Dettach global click out event.
   */
  dettachGlobalClickOut(): void {
    window.removeEventListener('click', this.onClickOutOfWidget);
  }

  /**
   * Handle the user clicking out of the overlay.
   */
  onClickOutOfWidget = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-product-widgets]') && this.isOpen) {
      this.closeWidget();
    }
  };

  /* ===================================
  QUANTITY
  ====================================*/
  getQuantity(): number {
    return parseInt(this.elements.addToCartQuantitySelect.value);
  }

  /* ===================================
  VARIANTS
  ====================================*/
  /**
   * Handles the variant change event.
   */
  onVariantChange(): void {
    // Toggle the add button.
    this.toggleAddButton(!this.variantSelects.currentVariant.available);
  }

  /**
   * Controls if the add to basket button is enabled
   * @param disable Set to true for add button to be disabled and show out of stock
   */
  toggleAddButton(disable = true): void {
    if (disable) {
      this.elements.addToCartButton.setAttribute('disabled', 'disabled');
      this.elements.addToCartButtonText.textContent = 'Out of Stock';
    } else {
      this.elements.addToCartButton.removeAttribute('disabled');
      this.elements.addToCartButtonText.textContent = 'Add to cart';
    }
  }

  /* ===================================
  ADD TO CART
  ====================================*/
  /**
   * Submits product selection to Shopify API
   * @param event - Submit event.
   */
  onSubmitHandler(event: Event): void {
    // Prevent default form submission
    event.preventDefault();
    if (this.elements.addToCartButton.classList.contains('loading')) return;

    this.elements.addToCartButton.setAttribute('aria-disabled', 'true');

    const postData: AddToCartRequest = {
      items: [
        {
          id: this.variantSelects
            ? this.variantSelects.currentVariant.id
            : this.initialVariantId,
          quantity: this.getQuantity(),
        },
      ],
    };

    // Handle Recharge
    if (this.elements.rechargeFrequency) {
      postData.items[0].properties = {
        shipping_interval_frequency: this.elements.rechargeFrequency.value,
        shipping_interval_unit_type: this.elements.rechargeUnitType.value,
      };
    }

    // Add the item to the cart
    shopifyService.addToCart(postData).then(() => {
      this.elements.addToCartButton.removeAttribute('aria-disabled');
      this.closeWidget();
    });
  }
}

/**
 * Initiates product cards
 */
export const bindProductWidgets = (): void => {
  // Select the elements.
  const productWidgets = document.querySelectorAll('[data-product-card]');
  // Loop through the elements.
  productWidgets.forEach((el) => {
    // Create a new instance of the ProductWidget class.
    new ProductWidget(el as HTMLElement);
  });
};
